import {Medal} from './leaderboardTheme';

// 'streaks' is the third (Top Streaks) tab — same get_leaderboard contract,
// ranking_criteria='streak_count', duration ignored server-side.
export type Period = 'daily' | 'weekly' | 'streaks';

export type RankingCriteria = 'game_wins' | 'streak_count';

export type TrendDir = 'up' | 'down' | 'flat';

// One ranked player as delivered by the data source (mock engine or API).
// trend/trendDelta are the server's since-yesterday movement when available
// (live mode); the mock engine omits them and useLeaderboardData falls back to
// a session-relative baseline.
export interface LeaderboardEntry {
  userid: number;
  rank: number;
  name: string;
  rating: number;
  wins: number;
  avatarUrl?: string;
  you?: boolean;
  trend?: TrendDir;
  trendDelta?: number | null;
}

// The signed-in player's own standing (may sit outside the returned top list).
export interface MeEntry {
  // null ⇒ no wins in the window yet, hence unranked.
  rank: number | null;
  wins: number;
  rating: number;
  name: string;
  avatarUrl?: string;
  inTop: boolean;
  trend?: TrendDir;
  trendDelta?: number | null;
}

// A full snapshot of one period's board.
export interface LeaderboardSnapshot {
  period: Period;
  entries: LeaderboardEntry[];
  me: MeEntry;
  totalPlayers: number;
  leaderWins: number;
}

// Entry decorated with presentation state by useLeaderboardData.
export interface LeaderboardRowVM extends LeaderboardEntry {
  initials: string;
  color: string;
  medal: Medal | null;
  barPct: number;
  flash: boolean;
  trend: 'up' | 'down' | 'flat';
  trendAbs: number | null;
}

export interface MeVM extends MeEntry {
  initials: string;
  trend: 'up' | 'down' | 'flat';
  trendAbs: number | null;
  // Wins needed to overtake the player one rank above (0 when leader/unranked).
  toNext: number;
  nextLabel: string;
  winsToEnterTop: number;
}

export interface PeriodMeta {
  key: Period;
  label: string;
  word: string;
  winWord: string;
  criteria: RankingCriteria;
  // Column header for the rightmost metric column ("WINS" vs "DAYS").
  metricLabel: string;
  // Trend arrows aren't meaningful for a point-in-time streak count.
  showTrend: boolean;
  rankedBySubtitle: string;
  emptyText: string;
}

export const PERIODS: PeriodMeta[] = [
  {
    key: 'daily',
    label: 'Daily',
    word: 'day',
    winWord: 'today',
    criteria: 'game_wins',
    metricLabel: 'WINS',
    showTrend: true,
    rankedBySubtitle: 'Ranked by wins today',
    emptyText: 'No wins today yet — win a game to top the board!',
  },
  {
    key: 'weekly',
    label: 'Weekly',
    word: 'week',
    winWord: 'this week',
    criteria: 'game_wins',
    metricLabel: 'WINS',
    showTrend: true,
    rankedBySubtitle: 'Ranked by wins this week',
    emptyText: 'No wins this week yet — win a game to top the board!',
  },
  {
    key: 'streaks',
    label: 'Top Streaks',
    word: 'streak',
    winWord: 'streak',
    criteria: 'streak_count',
    metricLabel: 'DAYS',
    showTrend: false,
    rankedBySubtitle: 'Ranked by current streak',
    emptyText: 'No active streaks yet — win a game today to start one!',
  },
];

export const periodMeta = (key: Period): PeriodMeta =>
  PERIODS.find(p => p.key === key) || PERIODS[0];

// ---------------------------------------------------------------------------
// Engagement summary (Phase 3: POST /dashboard/v1/engagement-summary/)
// ---------------------------------------------------------------------------

export type UserSegment = 'classroom' | 'casual';

export interface StreakCriterionVM {
  id: number;
  name: string;
  description: string;
  targetCount: number;
  userCount: number;
}

export interface MilestonePrize {
  // Stable prize-catalog reference (Prizes v2 P1: hydrated from
  // cc_engagement_prizes when the milestone config has a prize_code).
  // Optional/unused by the client today — carried through for forward compat.
  code?: string;
  title: string;
  short: string;
  imageUrl?: string;
  description?: string;
}

export interface MilestoneVM {
  id: string;
  days: number;
  period: 'weekly' | 'monthly' | string;
  daysLeft: number;
  achievedThisPeriod: boolean;
  prize: MilestonePrize;
  // 'week_checkpoint' for the day-14/day-21 progress markers (week_2/week_3)
  // — not real prize draws, just streak checkpoints on the way to the
  // monthly grand draw. Undefined for the weekly_draw/monthly milestones.
  kind?: string;
  // Display name for checkpoint milestones (e.g. "Week 2"/"Week 3") — prefer
  // this over prize.short/title when present, since checkpoints don't carry
  // a real prize.
  label?: string;
}

export interface NextMilestoneVM {
  id: string;
  daysLeft: number;
  prizeShort: string;
  label?: string;
}

// Weekly Top-3 leaderboard prize (Prizes v2) — a deterministic rank award,
// separate from the streak-draw milestones above. Not part of the milestones[]
// ledger (no streak requirement), so it's its own optional field: absent when
// the prize catalog has no active leaderboard-rank row for this segment, in
// which case the rewards section simply omits the card rather than guessing
// at copy ops hasn't configured yet.
export interface LeaderboardPrizeVM {
  code?: string;
  title: string;
  minMetricValue: number;
  rankFrom: number;
  rankTo: number;
}

export interface EngagementSummary {
  segment: UserSegment;
  streak: {
    streakCount: number;
    lastStreakDate: string | null;
    todayComplete: boolean;
    criteria: StreakCriterionVM[];
  };
  milestones: MilestoneVM[];
  nextMilestone: NextMilestoneVM | null;
  leaderboardPrize?: LeaderboardPrizeVM;
  // Any prize has ever been awarded (Prizes v2) — gates whether the Winners
  // section (and its own query) mounts at all; see useWinnersData's `enabled`
  // option / WinnersSection's `enabled` prop.
  winnersAvailable: boolean;
  pageEnabled: boolean;
  serverDate: string;
}

// A page_enabled=false / fetch-failed summary — every NEW section stays
// hidden and the screen renders exactly like the pre-engagement leaderboard.
export const DISABLED_ENGAGEMENT_SUMMARY: EngagementSummary = {
  segment: 'casual',
  streak: {
    streakCount: 0,
    lastStreakDate: null,
    todayComplete: false,
    criteria: [],
  },
  milestones: [],
  nextMilestone: null,
  winnersAvailable: false,
  pageEnabled: false,
  serverDate: new Date().toISOString(),
};

// ---------------------------------------------------------------------------
// Winners (Prizes v2 P3: POST /dashboard/v1/engagement-winners/)
// ---------------------------------------------------------------------------

// 'leaderboard' = deterministic Top-3-by-wins award; 'draw' = lucky draw among
// that period's streak-milestone eligibles.
export type WinnerSource = 'leaderboard' | 'draw';
export type WinnerPeriodType = 'weekly' | 'monthly';

export interface WinnerVM {
  userid: number;
  name: string;
  avatarUrl?: string;
  source: WinnerSource;
  // Rank 1-3 for leaderboard winners; null for draw winners (no ranking).
  rank: number | null;
  // Wins at award time for source='leaderboard'. The backend never records a
  // metric (e.g. streak_count) for source='draw' winners today, so this is
  // null for every draw winner — NOT a placeholder for "streak-day count"
  // (see WinnersSection's metric-label rendering).
  metricValue: number | null;
  prize: MilestonePrize;
  // This period's winner is the signed-in player — drives the YOU tag/highlight.
  you: boolean;
}

export interface WinnerPeriodVM {
  periodType: WinnerPeriodType;
  periodStart: string;
  periodEnd: string;
  // Built ONLY from periodStart/periodEnd (e.g. "Jul 20 – Jul 26" / "June
  // 2026") — the periods payload has no period_key at all (it groups winners
  // by (period_type, period_start_date) instead, see get_engagement_winners_periods),
  // and even where a period_key exists elsewhere in the contract its
  // weekly/monthly rollover clock is a few hours offset from the leaderboard
  // week (Prizes v2 plan risk #3) — labels must never be derived from it.
  label: string;
  winners: WinnerVM[];
}

// A single past win, as surfaced in `me.wins[]` — unused by any UI today
// (carried through for forward compat), so this only needs to be a truthful
// mapping of what build_me_winners actually returns, snake_case -> camelCase.
export interface MeWinVM {
  periodType: WinnerPeriodType;
  periodStartDate: string;
  periodEndDate: string;
  prize: MilestonePrize;
  rank: number | null;
}

export interface WinnersMeVM {
  hasWon: boolean;
  wins: MeWinVM[];
}

export interface EngagementWinners {
  periods: WinnerPeriodVM[];
  me: WinnersMeVM;
  serverDate: string;
}

// No winners yet / fetch failed — the Winners section renders its own
// empty state for an empty periods[], so this is a safe default either way.
export const DISABLED_ENGAGEMENT_WINNERS: EngagementWinners = {
  periods: [],
  me: {hasWon: false, wins: []},
  serverDate: new Date().toISOString(),
};

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const MONTH_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Period label formatter shared by the live mapping and the mock — the ONE
// place that turns period_start/period_end dates into display copy, so
// neither path is tempted to parse period_key instead (see WinnerPeriodVM).
export function formatWinnerPeriodLabel(
  periodType: WinnerPeriodType,
  periodStart: string,
  periodEnd: string,
): string {
  const start = new Date(periodStart);
  const end = new Date(periodEnd);
  if (periodType === 'monthly') {
    return `${MONTH_FULL[start.getUTCMonth()]} ${start.getUTCFullYear()}`;
  }
  return `${MONTH_ABBR[start.getUTCMonth()]} ${start.getUTCDate()} – ${
    MONTH_ABBR[end.getUTCMonth()]
  } ${end.getUTCDate()}`;
}
